"use client";

import React, { useEffect } from "react";
import "@/assets/stylesheets/application.scss";
import Header from "@/components/layouts/Header";
import Footer from "@/components/layouts/Footer";
import GoogleTagManager from "@/components/application/tracking/GoogleTagManager";
import GoogleTagManagerNoscript from "@/components/application/tracking/GoogleTagManagerNoscript";
// import Intercom from "@/components/application/Intercom";
import SchemaMarkup from "@/components/application/SchemaMarkup";
import { logger } from '@/lib/logger';
import {
  AccreditationSmallSection,
  Footer as FooterBulletproof,
  Header as HeaderBulletproof,
} from "@/.storyblok/types/319751/storyblok-components";
import { usePathname } from "next/navigation";
import Mailchimp from "@/components/application/tracking/Mailchimp";
import Outfunnel from "@/components/application/tracking/Outfunnel";
import { CustomScriptRenderer } from "@/components/CustomScriptRenderer";
import { SbBlokData } from "@storyblok/react";

const stylesheets = {
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  blog: () => import('@/assets/stylesheets/blog.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  cyberEssentials: () => import('@/assets/stylesheets/cyber-essentials.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  event: () => import('@/assets/stylesheets/event.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  glossary: () => import('@/assets/stylesheets/glossary.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  progressIndicator: () => import('@/assets/stylesheets/progress-indicator.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  purchases: () => import('@/assets/stylesheets/purchases.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  salesPoints: () => import('@/assets/stylesheets/sales-points.scss')
} as const;

interface ApplicationLayoutProps {
  children: React.ReactNode;
  heroContent?: React.ReactNode;
  styleController?: string;
  pageTitle?: string;
  pageDescription?: string;
  pageImage?: string;
  metadata?: any;
  header?: HeaderBulletproof;
  footer?: FooterBulletproof;
  trustsmall?: AccreditationSmallSection;
  noIndex?: boolean;
  adLandingPage?: boolean;
  scripts?: any;
  schemaMarkupBlog?: any;
  schemaMarkupEvent?: any;
  schemaMarkupProduct?: any;
}

const ApplicationLayout: React.FC<ApplicationLayoutProps> = ({
  children,
  heroContent,
  styleController = "",
  pageTitle = "Bulletproof.co.uk",
  pageDescription = "Trusted UK cyber security & compliance services help you stay ahead of the hackers, take control of your infrastructure and protect your business data.",
  pageImage = "/social-markup/default-social.png",
  metadata,
  header,
  footer,
  trustsmall,
  noIndex = false,
  adLandingPage = false,
  scripts,
  schemaMarkupBlog,
  schemaMarkupEvent,
  schemaMarkupProduct,
  }) => {
  const pathname = usePathname();
  const canonicalUrl = process.env.NEXT_PUBLIC_BASE_URL
    ? `${process.env.NEXT_PUBLIC_BASE_URL}${pathname}`
    : `http://localhost:3000${pathname}`;
  const usBaseUrl = "https://www.targetdefense.com";
  const ukBaseUrl = "https://www.bulletproof.co.uk";
  const localizedPath = pathname === "/" ? "/" : pathname;
  const usAlternateUrl = `${usBaseUrl}${localizedPath}`;
  const ukAlternateUrl = `${ukBaseUrl}${localizedPath}`;

  const title = metadata?.title || pageTitle;
  const siteTitle = "Bulletproof.co.uk";
  const fullTitle = title?.toLowerCase().includes(siteTitle.toLowerCase())
    ? title
    : `${title} – ${siteTitle}`;
  const description = metadata?.description || pageDescription;
  const ogTitle = metadata?.openGraph?.title || title;
  const ogDescription = metadata?.openGraph?.description || description;
  const ogImage = metadata?.openGraph?.images?.[0]?.url || pageImage;
  const twitterTitle = metadata?.twitter?.title || ogTitle;
  const twitterDescription = metadata?.twitter?.description || ogDescription;
  const twitterImage = metadata?.twitter?.images?.[0]?.url || ogImage;
  const twitterCard = metadata?.twitter?.card || "summary_large_image";

  useEffect(() => {
    const loadStylesheet = async () => {
      try {
        if (pathname?.startsWith('/cyber-essentials')) {
          await stylesheets.cyberEssentials();
        } else if (pathname?.startsWith('/blog')) {
          await stylesheets.blog();
        } else if (pathname?.startsWith('/events')) {
          await stylesheets.event();
        } else if (pathname?.startsWith('/jargon-buster')) {
          await stylesheets.glossary();
        } else if (pathname?.startsWith('/purchases')) {
          await stylesheets.purchases();
          await stylesheets.progressIndicator();
        } else if (pathname?.startsWith('/partners')) {
          await stylesheets.salesPoints();
        }
      } catch (error) {
        logger.error('Error loading stylesheet:', error);
      }
    };

    loadStylesheet();
  }, [pathname]);

  return (
    <html className="no-js" lang="en">
    <head>
      <GoogleTagManager />

      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="preconnect" href="https://www.googletagmanager.com" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={metadata?.twitter?.site || "@bulletproofsec"} />
      <meta name="twitter:creator" content={metadata?.twitter?.creator || "@bulletproofsec"} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />

      {/* Open Graph Meta Tags */}
      <meta property="og:site_name" content="Bulletproof.co.uk" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:type" content={metadata?.openGraph?.type || "website"} />
      <meta property="og:url" content={metadata?.openGraph?.url || canonicalUrl} />
      <meta property="og:image" content={ogImage} />

      {noIndex && (
        <meta name="robots" content="noindex, nofollow" />
      )}

      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="ahrefs-site-verification" content="5d88f354cdf5da2b0f22b6b94d139c4ea610277299b2b4bb70cf00eca8352160" />

      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
      <meta name="msapplication-TileColor" content="#324450" />
      <meta name="theme-color" content="#324450" />
      <link rel="alternate" hrefLang="en-US" href={usAlternateUrl} />
      <link rel="alternate" hrefLang="en-GB" href={ukAlternateUrl} />
      <link rel="alternate" hrefLang="x-default" href={ukAlternateUrl} />

      <link rel="canonical" href={canonicalUrl} />
      {!noIndex && <SchemaMarkup
        blogPost={schemaMarkupBlog}
        eventDetails={schemaMarkupEvent}
        productDetails={schemaMarkupProduct}
      />}
    </head>

    <body className={styleController}>
    {scripts && <CustomScriptRenderer customScriptBlock={scripts} />}
    <GoogleTagManagerNoscript />
    <Mailchimp />
    <Outfunnel />

    {!adLandingPage && header && (
      <header>
        <Header
          heroContent={heroContent}
          blok={header as HeaderBulletproof & SbBlokData}
          trustsmall={trustsmall}
          headerClass={styleController}
        />
      </header>
    )}

    <main>
      {children}
    </main>

    {!adLandingPage && footer && <Footer blok={footer as FooterBulletproof & SbBlokData} />}
    {/*<Intercom />*/}
    </body>
    </html>
  );
};

export default ApplicationLayout;
